import CouponModal from '@components/coupons/coupon-modal/coupon-modal';
import {
  v2_Coupon2,
  v2_FallbackProvider as FallbackProvider,
  v2_fetchCouponsFallback2,
  v2_useCoupons2,
} from '@crocodille/common';
import { assert, isSome } from '@crocodille/util';
import { AppPage } from '@framework/page';
import PageMeta from '@framework/page-meta';
import { getApp } from '@utils/app';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CouponList from '../../components/coupons/coupon-list/coupon-list';

export interface CouponPageProps {
  readonly fallback: Record<string, unknown>;
}

const CouponPage: AppPage<CouponPageProps> = () => {
  const { query } = useRouter();

  const { latitude: latitudeSrc, longitude: longitudeSrc } = query;

  const customerLocation = useMemo(() => {
    if (!longitudeSrc || !latitudeSrc) {
      return null;
    }

    assert(
      typeof longitudeSrc === 'string' && typeof latitudeSrc === 'string',
      'latitude and longitude must be strings',
    );

    return {
      longitude: parseFloat(longitudeSrc),
      latitude: parseFloat(latitudeSrc),
    } as const;
  }, [longitudeSrc, latitudeSrc]);

  const {
    data: coupons,
    tagCounts,
    allTags,
    allCouponCount,
    tagFilter,
    toggleTagFilter,
    resetTagFilter,
  } = v2_useCoupons2({
    customerLocation,
  });
  const { t } = useTranslation();
  const [coupon, setCoupon] = useState(null as v2_Coupon2 | null);

  return (
    <>
      <PageMeta pageTitle={t('coupons.page_title')} description={''} />
      <CouponList
        tagCounts={tagCounts}
        allTags={allTags}
        allCouponCount={allCouponCount}
        tagFilter={tagFilter}
        toggleTagFilter={toggleTagFilter}
        resetTagFilter={resetTagFilter}
      >
        {coupons?.map?.((coupon) => (
          <CouponList.Item
            key={coupon.id}
            id={coupon.id}
            imagePath={coupon.imageDetailPath ?? null}
            name={coupon.name}
            description={coupon.descriptionHtml}
            tags={coupon.tags}
            discountText={[coupon.discountText, coupon.discountText2]
              .filter(isSome)
              .join(' ')}
            query={query}
            onClick={() => setCoupon(coupon)}
          />
        ))}
      </CouponList>
      <CouponModal coupon={coupon} onClose={() => setCoupon(null)} />
    </>
  );
};

CouponPage.getLayout = (page, pageProps) => {
  return (
    <FallbackProvider fallback={pageProps.fallback}>{page}</FallbackProvider>
  );
};

export const getServerSideProps: GetServerSideProps<CouponPageProps> = async (
  ctx,
) => {
  const app = getApp(ctx);

  const couponsFallback = await v2_fetchCouponsFallback2(app);

  return {
    props: { fallback: couponsFallback },
  };
};

export default CouponPage;
